/**
 * P2.9: useIdleTimeout
 *
 * Detects user inactivity and calls `onTimeout` after `timeoutMs` milliseconds
 * of no activity. Activity events tracked: mousemove, mousedown, keydown,
 * touchstart, scroll, visibilitychange (un-hide resets the timer).
 *
 * Usage:
 *   useIdleTimeout({ timeoutMs: 10 * 60 * 1000, onTimeout: logout });
 *
 * The hook is disabled when `enabled` is false (e.g., on the active session
 * page where the doctor should not be logged out mid-consultation).
 */

import { useEffect, useRef, useCallback } from 'react';

interface UseIdleTimeoutOptions {
  /** Milliseconds of inactivity before onTimeout fires. Default: 10 minutes. */
  timeoutMs?: number;
  /** Callback invoked when the idle timeout fires. */
  onTimeout: () => void;
  /** Whether the hook is active. Default: true. */
  enabled?: boolean;
}

// 'visibilitychange' fires on Document and Window alike in all browsers.
// Typed as string[] so the generic window.addEventListener overload accepts it.
const ACTIVITY_EVENTS: string[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'visibilitychange',
];

export function useIdleTimeout({
  timeoutMs = 10 * 60 * 1000,
  onTimeout,
  enabled = true,
}: UseIdleTimeoutOptions): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep a stable ref to onTimeout so we never need to re-subscribe events
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const resetTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    if (!enabled) return;

    // Start the timer immediately on mount
    resetTimer();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, resetTimer, { passive: true });
    }

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, resetTimer);
      }
    };
  }, [enabled, resetTimer]);
}
